import {
  Box,
  Divider,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
} from "@mui/icons-material";
import { Form, Formik } from "formik";
import { FC, Suspense, useCallback, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import * as yup from "yup";

import { SourceTagReferenceModal_AddMutation } from "~/src/__relay_artifacts__/SourceTagReferenceModal_AddMutation.graphql";
import { SourceTagReferenceModal_DeleteMutation } from "~/src/__relay_artifacts__/SourceTagReferenceModal_DeleteMutation.graphql";
import { SourceTagReferenceModal_Query } from "~/src/__relay_artifacts__/SourceTagReferenceModal_Query.graphql";
import { SourceTagReferenceModal_UpdateMutation } from "~/src/__relay_artifacts__/SourceTagReferenceModal_UpdateMutation.graphql";
import { GhostButton, SolidButton } from "~/src/components/common/Button";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";
import { Modal } from "~/src/components/common/Modal";
import {
  Table,
  TableDataColumn,
  TableDataRow,
  TableHeaderColumn,
  TableHeaderRow,
} from "~/src/components/common/tables/Table";
import { TableActions } from "~/src/components/common/tables/TableActions";
import { usePopupImportSessionSourceTags } from "~/src/components/features/popup/PopupImport/PopupImportSessionSourceTagsContext";
import { useMutationCommit } from "~/src/lib/react-relay";
import { formatUnknownError } from "~/src/lib/utils/formatUnknownError";

const query = graphql`
  query SourceTagReferenceModal_Query($funnelSlug: String!) {
    funnel(slug: $funnelSlug) {
      name
      sourceTags(first: 500) {
        edges {
          node {
            id
            key
            value
            source {
              id
              name
            }
            pageUrl
          }
        }
      }
    }
  }
`;

const addMutation = graphql`
  mutation SourceTagReferenceModal_AddMutation($input: AddSourceTagInput!) {
    addSourceTag(input: $input) {
      sourceTag {
        id
        key
        value
        source {
          id
          name
        }
        pageUrl
      }
    }
  }
`;

const updateMutation = graphql`
  mutation SourceTagReferenceModal_UpdateMutation(
    $input: UpdateSourceTagInput!
  ) {
    updateSourceTag(input: $input) {
      sourceTag {
        id
        key
        value
        source {
          id
          name
        }
        pageUrl
      }
    }
  }
`;

const deleteMutation = graphql`
  mutation SourceTagReferenceModal_DeleteMutation(
    $input: DeleteSourceTagInput!
  ) {
    deleteSourceTag(input: $input) {
      deletedSourceTagId
    }
  }
`;

const tagFormSchema = yup.object({
  parameterName: yup.string().required("パラメータ名を入力してください"),
  parameterValue: yup.string().required("値を入力してください"),
});

type TagFormValues = yup.InferType<typeof tagFormSchema>;

export type SourceTagNode = {
  id: string;
  key: string | null;
  value: string | null;
  source: { id: string; name: string };
  pageUrl: string;
};

export type EditingSourceTag = {
  id: string;
  parameterName: string;
  parameterValue: string;
};

function buildSourceTagUrl(sourceTag: SourceTagNode): string {
  if (sourceTag.key && sourceTag.value) {
    return `${sourceTag.pageUrl}?${sourceTag.key}=${sourceTag.value}`;
  }
  return sourceTag.pageUrl;
}

function isDuplicateSourceTagError(message: string): boolean {
  return message.includes("同一のパラメータ名と値は登録できません");
}

type SourceTagTableRowProps = {
  sourceTag: SourceTagNode;
  sourceId: string;
  funnelSlug: string;
  isEditable: boolean;
  onRequestEdit: (tag: SourceTagNode) => void;
  onDeleted: () => void;
};

const SourceTagTableRow: FC<SourceTagTableRowProps> = ({
  sourceTag,
  sourceId,
  funnelSlug,
  isEditable,
  onRequestEdit,
  onDeleted,
}) => {
  const toast = useToast();
  const { unregisterSessionSourceTag } = usePopupImportSessionSourceTags();
  const deleteMutate =
    useMutationCommit<SourceTagReferenceModal_DeleteMutation>(deleteMutation);

  const handleStartEdit = useCallback(() => {
    onRequestEdit(sourceTag);
  }, [onRequestEdit, sourceTag]);

  const handleDelete = useCallback(async () => {
    try {
      const res = await deleteMutate({
        variables: {
          input: { sourceTagId: sourceTag.id },
        },
      });
      if (!res.deleteSourceTag?.deletedSourceTagId) {
        throw new Error("assertion failed");
      }
      unregisterSessionSourceTag(funnelSlug, sourceId, sourceTag.id);
      toast({
        title: "入稿用リンクを削除しました",
        status: "success",
      });
      onDeleted();
    } catch (err) {
      toast({
        title: "入稿用リンクの削除に失敗しました",
        description: formatUnknownError(err),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [
    deleteMutate,
    funnelSlug,
    onDeleted,
    sourceId,
    sourceTag.id,
    toast,
    unregisterSessionSourceTag,
  ]);

  const actionMenus = useMemo(
    () =>
      isEditable
        ? [
            {
              label: "編集",
              icon: <EditOutlinedIcon />,
              onClick: handleStartEdit,
            },
            {
              label: "削除",
              icon: <DeleteOutlineIcon />,
              onClick: handleDelete,
            },
          ]
        : [],
    [handleDelete, handleStartEdit, isEditable]
  );

  const url = buildSourceTagUrl(sourceTag);

  return (
    <TableDataRow>
      <TableDataColumn type="text" data={sourceTag.source.name} />
      <TableDataColumn type="text" data={url} />
      <TableDataColumn
        type="actions"
        data={
          isEditable ? <TableActions actionMenus={actionMenus} /> : undefined
        }
      />
    </TableDataRow>
  );
};

type SourceTagListProps = {
  funnelSlug: string;
  sourceId: string;
  fetchKey: number;
  onRequestEdit: (tag: SourceTagNode) => void;
  onListChanged: () => void;
  onClearEdit: () => void;
};

const SourceTagList: FC<SourceTagListProps> = ({
  funnelSlug,
  sourceId,
  fetchKey,
  onRequestEdit,
  onListChanged,
  onClearEdit,
}) => {
  const { isSessionSourceTag } = usePopupImportSessionSourceTags();

  const data = useLazyLoadQuery<SourceTagReferenceModal_Query>(
    query,
    { funnelSlug },
    { fetchKey, fetchPolicy: "network-only" }
  );

  const sourceTags =
    data.funnel?.sourceTags.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => node != null)
      .filter((tag) => tag.source.id === sourceId) ?? [];

  const handleDeleted = useCallback(() => {
    onClearEdit();
    onListChanged();
  }, [onClearEdit, onListChanged]);

  if (sourceTags.length === 0) {
    return (
      <Text fontSize="14px" color="gray.500" py="16px" textAlign="center">
        この流入元の入稿用リンクはありません
      </Text>
    );
  }

  return (
    <Table>
      <TableHeaderRow>
        <TableHeaderColumn>流入元</TableHeaderColumn>
        <TableHeaderColumn>URL</TableHeaderColumn>
        <TableHeaderColumn>操作</TableHeaderColumn>
      </TableHeaderRow>
      {sourceTags.map((sourceTag) => {
        const editable = isSessionSourceTag(funnelSlug, sourceId, sourceTag.id);
        return (
          <SourceTagTableRow
            key={sourceTag.id}
            sourceTag={sourceTag}
            sourceId={sourceId}
            funnelSlug={funnelSlug}
            isEditable={editable}
            onRequestEdit={onRequestEdit}
            onDeleted={handleDeleted}
          />
        );
      })}
    </Table>
  );
};

type SourceTagFormProps = {
  funnelSlug: string;
  sourceName: string;
  landingPageId: string;
  sourceId: string;
  editingTag: EditingSourceTag | null;
  onCancelEdit: () => void;
  onSaved: () => void;
};

const SourceTagForm: FC<SourceTagFormProps> = ({
  funnelSlug,
  sourceName,
  landingPageId,
  sourceId,
  editingTag,
  onCancelEdit,
  onSaved,
}) => {
  const toast = useToast();
  const { registerSessionSourceTag } = usePopupImportSessionSourceTags();
  const addMutate =
    useMutationCommit<SourceTagReferenceModal_AddMutation>(addMutation);
  const updateMutate =
    useMutationCommit<SourceTagReferenceModal_UpdateMutation>(updateMutation);

  const isEditing = editingTag != null;
  const formKey = editingTag?.id ?? "create";

  const initialValues = useMemo<TagFormValues>(
    () => ({
      parameterName: editingTag?.parameterName ?? "",
      parameterValue: editingTag?.parameterValue ?? "",
    }),
    [editingTag]
  );

  const handleSubmit = useCallback(
    async (values: TagFormValues, { resetForm }: { resetForm: () => void }) => {
      const key = values.parameterName.trim();
      const value = values.parameterValue.trim();

      try {
        if (isEditing && editingTag) {
          await updateMutate({
            variables: {
              input: {
                sourceTagId: editingTag.id,
                sourceId,
                key,
                value,
              },
            },
          });
          toast({
            title: "入稿用リンクを更新しました",
            status: "success",
          });
          onCancelEdit();
          onSaved();
          return;
        }

        const res = await addMutate({
          variables: {
            input: {
              pageId: landingPageId,
              sourceId,
              key,
              value,
            },
          },
        });
        const tagId = res.addSourceTag?.sourceTag?.id;
        if (!tagId) {
          throw new Error("assertion failed");
        }
        registerSessionSourceTag(funnelSlug, sourceId, tagId);
        toast({
          title: "入稿用リンクを追加しました",
          status: "success",
        });
        resetForm();
        onSaved();
      } catch (err) {
        const message = formatUnknownError(err);
        const isDuplicate = isDuplicateSourceTagError(message);
        toast({
          title: isDuplicate
            ? "同一のパラメータ名と値は登録できません"
            : isEditing
            ? "入稿用リンクの更新に失敗しました"
            : "入稿用リンクの追加に失敗しました",
          description: isDuplicate ? undefined : message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    },
    [
      addMutate,
      editingTag,
      funnelSlug,
      isEditing,
      landingPageId,
      onCancelEdit,
      onSaved,
      registerSessionSourceTag,
      sourceId,
      toast,
      updateMutate,
    ]
  );

  const handleCancelClick = useCallback(() => {
    onCancelEdit();
  }, [onCancelEdit]);

  return (
    <Formik<TagFormValues>
      key={formKey}
      initialValues={initialValues}
      validationSchema={tagFormSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <VStack align="stretch" spacing="12px">
            {isEditing ? (
              <Text fontSize="13px" fontWeight="semibold" color="gray.700">
                入稿用リンクを編集
              </Text>
            ) : null}
            <FormControl label="流入元">
              <Text fontSize="13px" color="gray.700">
                {sourceName}
              </Text>
            </FormControl>
            <VStack align="flex-start" spacing="12px">
              <Box w="100%">
                <FormControl required label="パラメータ名">
                  <FormikTextField
                    type="text"
                    mode="fill"
                    name="parameterName"
                  />
                </FormControl>
              </Box>
              <Box w="100%">
                <FormControl required label="値">
                  <FormikTextField
                    type="text"
                    mode="fill"
                    name="parameterValue"
                  />
                </FormControl>
              </Box>
              <HStack pt="8px" alignSelf="flex-end" spacing="8px">
                {isEditing ? (
                  <GhostButton type="button" onClick={handleCancelClick}>
                    キャンセル
                  </GhostButton>
                ) : null}
                <SolidButton type="submit" isLoading={isSubmitting}>
                  {isEditing ? "保存" : "追加"}
                </SolidButton>
              </HStack>
            </VStack>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

export type Props = {
  funnelSlug: string;
  funnelName: string;
  landingPageId: string;
  sourceId: string;
  sourceName: string;
  isOpen: boolean;
  onClose: () => void;
};

export const SourceTagReferenceModal: FC<Props> = ({
  funnelSlug,
  funnelName,
  landingPageId,
  sourceId,
  sourceName,
  isOpen,
  onClose,
}) => {
  const [listFetchKey, setListFetchKey] = useState(0);
  const [editingTag, setEditingTag] = useState<EditingSourceTag | null>(null);

  const handleListChanged = useCallback(() => {
    setListFetchKey((k) => k + 1);
  }, []);

  const handleClearEdit = useCallback(() => {
    setEditingTag(null);
  }, []);

  const handleRequestEdit = useCallback((tag: SourceTagNode) => {
    setEditingTag({
      id: tag.id,
      parameterName: tag.key ?? "",
      parameterValue: tag.value ?? "",
    });
  }, []);

  const handleModalClose = useCallback(() => {
    setEditingTag(null);
    onClose();
  }, [onClose]);

  const handleSaved = useCallback(() => {
    handleListChanged();
  }, [handleListChanged]);

  return (
    <Modal
      isOpen={isOpen}
      title={`${funnelName} | ${sourceName} | 入稿用リンク`}
      onClose={handleModalClose}
      width="760px"
    >
      <Box maxHeight="60vh" overflowY="auto">
        <VStack align="stretch" spacing="16px">
          <SourceTagForm
            funnelSlug={funnelSlug}
            sourceName={sourceName}
            landingPageId={landingPageId}
            sourceId={sourceId}
            editingTag={editingTag}
            onCancelEdit={handleClearEdit}
            onSaved={handleSaved}
          />
          <Divider />
          <Suspense
            fallback={
              <Box display="flex" justifyContent="center" py="24px">
                <Spinner />
              </Box>
            }
          >
            <SourceTagList
              funnelSlug={funnelSlug}
              sourceId={sourceId}
              fetchKey={listFetchKey}
              onRequestEdit={handleRequestEdit}
              onListChanged={handleListChanged}
              onClearEdit={handleClearEdit}
            />
          </Suspense>
        </VStack>
      </Box>
      <HStack spacing="8px" justifyContent="flex-end" mt="16px">
        <GhostButton onClick={handleModalClose}>閉じる</GhostButton>
      </HStack>
    </Modal>
  );
};
